import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Text,
  Stack,
  Card,
  NumberInput,
  Button,
  Center,
} from '@mantine/core';
import { useTranslation, Trans } from 'react-i18next';
import { useState, useEffect, useMemo } from 'react';
import {
  IconMoneybag, IconBusinessplan, IconTrendingDown, IconTrophy,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import {
  ProcessHeader, Page, AmountSlider, MortgageInfoBoxes,
} from '@/components';
import { useIdInStoreGuard } from '@/hooks/useIdfromUrl';
import { getRouteById, routeWithParams } from '@/helpers/routes';
import routes from '@/routes';
import {
  useCalculateCycleMortgageRepaymentTableMutation,
  useSaveMutation,
  useMortgageRefinancingContactMutation,
  useSendingToTheCustomerForSignatureMutation,
} from '@/services/api';
import { updateMortgageCycle } from '@/data/features/mortgageCycle/mortgageCycle';
import ProcessButton from '@/components/ProcessButton';
import classes from './mortgageCycleAction.module.css';
import leadsIcon from '@/assets/images/leads.svg?react'; // eslint-disable-line import/no-unresolved
import { MORTGAGE_CYCLE_ACTIONS } from '@/data/constants';

const MortgageCycleAction = () => {
  const { t } = useTranslation();
  const mortgageCycleDetails = useSelector((state) => state.mortgageCycle);
  const navigate = useNavigate();
  const proposal = useSelector((state) => state.proposal);
  const [desiredMonthlyPayment, setDesiredMonthlyPayment] = useState('');
  const [loading, setLoading] = useState(false);

  const [selectedPeriod, setSelectedPeriod] = useState(0);
  const dispatch = useDispatch();

  const { id, extraDetails, borrower } = proposal;
  const { email, phoneNumber, fullName } = borrower?.[0] || {};

  const { loanTypeNameHebrew, isRecycling } = extraDetails;
  const isCycleLoan = isRecycling === 'yes';

  useIdInStoreGuard();

  const {
    mortgageCycleAction,
    currentLoans,
    bank,
    mortageCycleForAllLoanInCycleWithAdditionalLoan,
  } = mortgageCycleDetails;

  // eslint-disable-next-line max-len
  const calculationData = isCycleLoan ? mortageCycleForAllLoanInCycleWithAdditionalLoan : mortgageCycleDetails.calculationData;

  // Use calculationData from state
  const totalsBeforeCalculation = calculationData?.totalsBeforeCalculation || null;
  const totalsAfterCalculation = calculationData?.totalsAfterCalculation || null;
  const totalsAfterCalculationPeriods = calculationData?.totalsAfterCalculationPeriods || null;

  // Set initial selectedPeriod to max when calculationData is available
  useEffect(() => {
    if (calculationData?.totalsAfterCalculationPeriods
      && calculationData.totalsAfterCalculationPeriods.length > 0) {
      const maxPeriod = Math.max(
        ...calculationData.totalsAfterCalculationPeriods.map((item) => item.monthsLeft || 0),
      );
      setSelectedPeriod(maxPeriod);
    }
  }, [calculationData]);

  const backPage = 'loansComparation';
  const handleReturn = () => {
    navigate(routeWithParams(getRouteById(routes.newProposal.id, backPage)?.path, { id }));
  };

  const [calculateCycleMortgageRepaymentTable] = useCalculateCycleMortgageRepaymentTableMutation();
  const [saveMortgageCycle] = useSaveMutation();
  const [apiContant] = useMortgageRefinancingContactMutation();
  const [apiConfigSendToClientForSignature] = useSendingToTheCustomerForSignatureMutation();

  // Calculate current monthly payment as sum of all current loans
  const currentMonthlyPayment = currentLoans?.reduce(
    (sum, loan) => sum + (Number(loan.monthlyPayment) || 0),
    0,
  ) || 0;

  const currentLoanAmount = currentLoans?.reduce(
    (sum, loan) => sum + (Number(loan.totalAmount) || 0),
    0,
  ) || 0;

  const handleBackButtonClick = () => {
    navigate(routeWithParams(getRouteById(routes.newProposal.id, backPage)?.path, { id }));
  };

  const getValueColor = (before, after, isLowerBetter = true) => {
    if (before === after) return 'gray';
    const improved = isLowerBetter ? after < before : after > before;
    return improved ? 'var(--mantine-color-green-7)' : 'var(--mantine-color-red-7)';
  };

  // Function to get data for selected period from totalsAfterCalculationPeriods array
  const getSelectedPeriodData = () => {
    if (!calculationData?.totalsAfterCalculationPeriods || selectedPeriod === 0) {
      return totalsAfterCalculation;
    }

    const selectedData = calculationData.totalsAfterCalculationPeriods.find(
      (item) => item.monthsLeft === Number(selectedPeriod),
    );

    return selectedData?.totalsAfterCalculation || totalsAfterCalculation;
  };

  const effectiveTotalsAfter = mortgageCycleAction === 'increasingTheLoanPeriod' || mortgageCycleAction === 'reductionOfRepaymentAndExtensionOfTheLoanTermThroughLoanConsolidation'
    ? getSelectedPeriodData()
    : totalsAfterCalculation;

  const mortgageBoxes = useMemo(() => {
    // eslint-disable-next-line max-len
    const beforeMonthly = Number(totalsBeforeCalculation?.newMonthlyPayment ?? currentMonthlyPayment);
    const afterMonthly = Number(effectiveTotalsAfter?.newMonthlyPayment ?? 0);
    const monthlySaving = (beforeMonthly - afterMonthly).toFixed(0);

    const beforeTotal = Number(totalsBeforeCalculation?.finalPayment ?? 0);
    const afterTotal = Number(effectiveTotalsAfter?.finalPayment ?? 0);
    const totalSaving = Math.round(beforeTotal - afterTotal);
    const totalSavingPercentage = beforeTotal > 0
      ? Number((((beforeTotal - afterTotal) / beforeTotal) * 100).toFixed(1))
      : 0;

    return [
      {
        title: t('pages.mortgageCycle.newMonthlyPayment'),
        value: `${Math.round(afterMonthly).toLocaleString()} ₪`,
        info: t('pages.mortgageCycle.newMonthlyPaymentInfo'),
        icon: <IconMoneybag color="#00ABAB" />,
        color: getValueColor(beforeMonthly, afterMonthly, true),
        borderColor: '#00ABAB',
      },
      {
        title: t('pages.mortgageCycle.monthlySaving'),
        value: `${Number(monthlySaving).toLocaleString()} ₪`,
        info: t('pages.mortgageCycle.monthlySavingInfo'),
        icon: <IconTrendingDown color="#00ABAB" />,
        color: monthlySaving > 0 ? 'var(--mantine-color-green-7)' : 'var(--mantine-color-red-7)',
        borderColor: '#00ABAB',
      },
      {
        title: t('pages.mortgageCycle.totalSaving'),
        value: `${totalSaving.toLocaleString()} ₪`,
        info: t('pages.mortgageCycle.totalSavingInfo', { percentage: totalSavingPercentage }),
        icon: <IconTrophy color="#00ABAB" />,
        color: totalSaving > 0 ? 'var(--mantine-color-green-7)' : 'var(--mantine-color-red-7)',
        show: mortgageCycleAction !== 'increasingTheLoanPeriod' && mortgageCycleAction !== 'reductionOfRepaymentAndExtensionOfTheLoanTermThroughLoanConsolidation',
        borderColor: '#00ABAB',
      },
      {
        title: t('pages.mortgageCycle.returnConversionTitle'),
        value: `${Number(effectiveTotalsAfter?.returnConversion).toFixed(2)} ₪`,
        info: t('pages.mortgageCycle.returnConversionInfo', { amount: totalsBeforeCalculation?.returnConversion?.toFixed(2) }),
        icon: <IconBusinessplan color="#00ABAB" />,
        // eslint-disable-next-line max-len
        color: getValueColor(totalsBeforeCalculation?.returnConversion, effectiveTotalsAfter?.returnConversion, true),
        borderColor: '#00ABAB',
      },
    ];
  // eslint-disable-next-line max-len
  }, [totalsBeforeCalculation, effectiveTotalsAfter, currentMonthlyPayment, mortgageCycleAction, t]);

  if (totalsAfterCalculationPeriods?.length === 0) {
    return (
      <Center>
        <Text size='md' fw={600} c='red' ta='center' mb='md'>
          {t('pages.mortgageCycleAction.cannotExtendLoanPeriod')}
        </Text>
        <Button onClick={handleBackButtonClick} size='sm' >
          {t('globals.back')}
        </Button>
      </Center>
    );
  }

  const sliderStep = 12;
  const minPeriodForSlider = Array.isArray(mortgageCycleDetails.currentLoans)
    && mortgageCycleDetails.currentLoans.length > 0
    ? Math.max(
      ...mortgageCycleDetails.currentLoans.map(
        (item) => item.monthsLeft || 0,
      ),
    )
    : 0;

  // Helper functions for slider calculations
  const getMinPeriod = () => {
    if (minPeriodForSlider % sliderStep === 0) {
      return minPeriodForSlider;
    }
    return minPeriodForSlider - (minPeriodForSlider % sliderStep) + sliderStep;
  };

  const getMaxPeriod = () => Math.max(
    ...calculationData.totalsAfterCalculationPeriods.map((item) => item.monthsLeft || 0),
  );
  let nextPage = isCycleLoan ? 'loanAmount' : 'success';

  const handleContinue = async () => {
    if (isCycleLoan) {
      navigate(routeWithParams(getRouteById(routes.newProposal.id, nextPage)?.path, { id }));
      return;
    }

    // For reducingTheLoanPeriod, show modal instead of proceeding
    if (mortgageCycleAction === 'reducingTheLoanPeriod') {
      const sendValues = {
        offerId: id,
        bank,
        email,
        currentLoanAmount: Math.round(currentLoanAmount * 100) / 100,
        loanType: loanTypeNameHebrew,
        phoneNumber,
        fullName,
        currentMonthlyPayment: Math.round(currentMonthlyPayment * 100) / 100,
        monthsLeft: minPeriodForSlider,
        desiredMonthlyPayment,
        mortgageCycleAction,
      };

      try {
        setLoading(true);
        const { error } = await apiContant(sendValues);
        if (error && !error.data.success) {
          throw new Error('Failed to send contact data');
        }
        const sendSummaryLinkObj = {
          phoneNumber,
          fullName,
          email,
          proposalNumber: id,
        };

        await apiConfigSendToClientForSignature(sendSummaryLinkObj);
        navigate(routeWithParams(getRouteById(routes.newProposal.id, nextPage).path, { id }));
      } catch {
        notifications.show({
          title: t('globals.error.title'),
          message: t('globals.error.message'),
          color: 'red',
        });
        setLoading(false);
      }
    } else {
      setLoading(true);
      try {
        // Send the calculationData structure

        const updatedLoansForSaveAndCalculation = (currentLoans || [])
          .map((l) => (l?.isBetterToChange === true
            ? { ...l, monthsLeft: selectedPeriod || l.monthsLeft }
            : l));

        // eslint-disable-next-line max-len
        const isSamePeriod = mortgageCycleDetails.mortgageCycleAction === MORTGAGE_CYCLE_ACTIONS.reducingRepayment || mortgageCycleDetails.mortgageCycleAction === MORTGAGE_CYCLE_ACTIONS.reductionRepaymentWithoutChangingTheTermByConsolidatingLoans;
        // eslint-disable-next-line max-len
        const isMergingLoans = mortgageCycleDetails.mortgageCycleAction === MORTGAGE_CYCLE_ACTIONS.reductionOfRepaymentAndExtensionOfTheLoanTermThroughLoanConsolidation || mortgageCycleDetails.mortgageCycleAction === MORTGAGE_CYCLE_ACTIONS.reductionRepaymentWithoutChangingTheTermByConsolidatingLoans;
        const result = await calculateCycleMortgageRepaymentTable(
          {
            updatedLoansForSaveAndCalculation,
            isSamePeriod,
            isMergingLoans,
          },
        ).unwrap();

        const totalsAfterCalculationNew = getSelectedPeriodData();

        const calculationDataForStore = result?.data?.calculationData || {
          totalsBeforeCalculation,
          totalsAfterCalculation: totalsAfterCalculationNew,
          totalsAfterCalculationPeriods,
        };

        const totalSavingForSave = Math.round(Number(calculationDataForStore
          .totalsBeforeCalculation?.finalPayment ?? 0)
          - Number(calculationDataForStore.totalsAfterCalculation?.finalPayment ?? 0));

        await saveMortgageCycle({
          id,
          totalSaving: totalSavingForSave,
          cycleMortgage: {
            loans: updatedLoansForSaveAndCalculation,
          },
        }).unwrap();

        // Store the result in the mortgage cycle store
        dispatch(updateMortgageCycle({
          ...mortgageCycleDetails,
          repaymentTableData: result.data,
          // Store calculationData if available in the result
          calculationData: calculationDataForStore,
        }));

        nextPage = isCycleLoan ? nextPage : 'borrowersDetails';
        // Navigate to the next step (you can change this to the appropriate route)
        navigate(routeWithParams(getRouteById(routes.newProposal.id, nextPage)?.path, { id }));
      } catch (err) {
        console.error('Error updating mortgage cycle:', err);
        notifications.show({
          title: t('globals.error.title'),
          message: t('globals.error.message'),
          color: 'red',
        });
      } finally {
        setLoading(false);
      }
    }
  };

  // Map mortgageCycleAction to header title key
  const actionTitleKeys = {
    reducingRepayment: 'pages.mortgageCycle.bankFileUpload.mortgageCycleAction.options.reducingRepaymentNoPeriodChangeNew',
    increasingTheLoanPeriod: 'pages.mortgageCycle.bankFileUpload.mortgageCycleAction.options.reducingRepaymentAndIncreasingTheLoanPeriod',
    reducingTheLoanPeriod: 'pages.mortgageCycle.bankFileUpload.mortgageCycleAction.options.increasingTheRepaymentAndReducingThePeriod',
  };

  // Calculate beforeMonthly for the reducingTheLoanPeriod case
  const beforeMonthly = Number(totalsBeforeCalculation?.newMonthlyPayment ?? currentMonthlyPayment);

  const cycleLoansComments = [
    t('pages.mortgageCycleAction.cycleWithAdditionalLoan.commentsList.first'),
    t('pages.mortgageCycleAction.cycleWithAdditionalLoan.commentsList.second'),
    t('pages.mortgageCycleAction.cycleWithAdditionalLoan.commentsList.third'),
  ];

  const headerText = isCycleLoan ? t('pages.mortgageCycleAction.cycleWithAdditionalLoan.title') : t('pages.mortgageCycleAction.title');
  const headerSubTitle = isCycleLoan ? t('pages.mortgageCycleAction.cycleWithAdditionalLoan.subtitle') : '';
  return (
    <ProcessHeader
      headerText={headerText}
      headerColor="#DCE0E5"
      headerTextColor="#00ABAB"
      subtitle={headerSubTitle}
      showReturnButton
      onReturn={handleReturn}
      icon={leadsIcon}
    >
      <form>
        <Page.Head
          title={t(actionTitleKeys[mortgageCycleAction])}
        />
        {
          mortgageCycleAction !== 'reducingTheLoanPeriod' && (
            <div>
              <MortgageInfoBoxes boxes={mortgageBoxes} />
            </div>
          )
        }

        {mortgageCycleAction === 'reducingTheLoanPeriod' && (
          // Special case for reducing loan period
          <>
            <Page.Section withBackground>
              <Card withBorder p="xl" radius="md">
                <Stack gap="lg">
                  <Text ta="center" size="lg" fw={600} c="blue">
                    {t('pages.mortgageCycle.currentMonthlyPayment')}
                  </Text>
                  <Text ta="center" size="xl" fw={700} c="red">
                    {beforeMonthly.toLocaleString()} ₪
                  </Text>
                  <Text ta="center" size="md" c="dimmed">
                    {t('pages.mortgageCycle.currentMonthlyPaymentDescription')}
                  </Text>
                </Stack>
              </Card>
            </Page.Section>

            <Page.Section withBackground>
              <Card withBorder p="xl" radius="md">
                <Stack gap="lg">
                  <Text ta="center" size="lg" fw={600} c="green">
                    {t('pages.mortgageCycle.desiredMonthlyPayment')}
                  </Text>
                  <NumberInput
                    placeholder={t('pages.mortgageCycle.enterDesiredAmount')}
                    value={desiredMonthlyPayment}
                    onChange={setDesiredMonthlyPayment}
                    min={0}
                    thousandSeparator=","
                    suffix=" ₪"
                    size="lg"
                    ta="center"
                  />
                  <Text ta="center" size="sm" c="dimmed">
                    {t('pages.mortgageCycle.desiredPaymentDescription')}
                  </Text>
                </Stack>
              </Card>
            </Page.Section>
          </>
        )}

        {(mortgageCycleAction === 'increasingTheLoanPeriod' || mortgageCycleAction === 'reductionOfRepaymentAndExtensionOfTheLoanTermThroughLoanConsolidation') && (
          // Special case for increasing loan period
          <>
            {calculationData?.totalsBeforeCalculation
              && calculationData?.totalsAfterCalculationPeriods && (
              <Page.Section withBackground>
                <div className={classes.sliderContainer}>
                  <div className={classes.sliderWrapper}>
                    <div className={classes.sliderTitle}>
                      <Text size="lg" fw={600} className={classes.sliderTitleText}>
                        בחרו לכמה חודשים תרצו להאריך את החזר המשכנתא
                      </Text>
                    </div>
                    <AmountSlider
                        min={getMinPeriod()}
                        max={getMaxPeriod()}
                        value={selectedPeriod}
                        onChange={setSelectedPeriod}
                        size="lg"
                        color="teal"
                        step={sliderStep}
                        showValue={false}
                        marksStep={12}
                        showYearBox={true}
                        yearBoxText={`${selectedPeriod} חודשים`}
                        labelAlwaysOn={false}
                      />
                  </div>
                </div>
                </Page.Section>
            )}
          </>
        )}
        {isCycleLoan && (
          <Page.Section>
            <Stack gap={4} pt={20} px='md'>
              <Text fw={700} size='sm'>{t('pages.mortgageCycleAction.cycleWithAdditionalLoan.comments')}</Text>
              {
                cycleLoansComments.map((comment, index) => (
                  <Text key={index}>
                    * <Trans
                      i18nKey={`pages.mortgageCycleAction.cycleWithAdditionalLoan.commentsList.${['first', 'second', 'third'][index]}`}
                      components={{ bold: <Text span fw={700} />, u: <u /> }}
                    />
                  </Text>
                ))
              }
            </Stack>
          </Page.Section>)}
        <Page.Actions>
          <ProcessButton
            text={mortgageCycleAction === 'reducingTheLoanPeriod' ? t('pages.businessCredit.transferToConsultant') : t('globals.continue')}
            onClick={handleContinue}
            loading={loading}
            disabled={(mortgageCycleAction === 'reducingTheLoanPeriod' && !desiredMonthlyPayment) && !desiredMonthlyPayment}
          />
        </Page.Actions>
      </form>
    </ProcessHeader>
  );
};

export default MortgageCycleAction;
